import React from 'react';
import { Link } from 'react-router-dom';
import { Target, TrendingUp, Users, MessageCircle, Sparkles, Heart } from 'lucide-react';
import './Corporate.css';

export const Corporate = () => {
  const benefits = [
    {
      icon: <Target size={32} />,
      title: 'Greater Focus',
      description: 'Employees with harmonized inner worlds bring enhanced concentration to their work.'
    },
    {
      icon: <TrendingUp size={32} />,
      title: 'Higher Productivity',
      description: 'Emotional wellness directly translates to improved work output and efficiency.'
    },
    {
      icon: <MessageCircle size={32} />,
      title: 'Better Communication',
      description: 'Healthy relationships foster clearer, more effective workplace interactions.'
    },
    {
      icon: <Users size={32} />,
      title: 'Healthier Teams',
      description: 'Connected and understood employees create stronger, more cohesive teams.'
    },
    {
      icon: <Sparkles size={32} />,
      title: 'Creative Resilience',
      description: 'When people feel safe and supported, creativity and innovation flourish.'
    },
    {
      icon: <Heart size={32} />,
      title: 'Stronger Results',
      description: 'Harmonized employees show up more effective, engaged, and committed to excellence.'
    }
  ];

  const workshops = [
    {
      title: 'Relationship Wellness for Employees',
      description: 'Navigate personal relationships with greater ease and understanding.',
      duration: 'Half-day or Full-day'
    },
    {
      title: 'Marriage & Partnership Harmony',
      description: 'Tools and practices for maintaining healthy, thriving relationships.',
      duration: 'Full-day workshop'
    },
    {
      title: 'Emotional Intelligence in the Workplace',
      description: 'Develop self-awareness and emotional regulation for professional success.',
      duration: 'Half-day workshop'
    },
    {
      title: 'Communication & Connection',
      description: 'Master the art of authentic communication in personal and professional life.',
      duration: 'Half-day workshop'
    },
    {
      title: 'Stress Management & Inner Balance',
      description: 'Practical tools for managing stress and maintaining emotional equilibrium.',
      duration: 'Half-day or Full-day'
    },
    {
      title: 'Custom Programs',
      description: 'Tailored workshops designed to meet your organization\'s specific needs.',
      duration: 'Flexible duration'
    }
  ];

  return (
    <div className="corporate-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">Corporate Wellness Programs</h1>
          <p className="body-large" style={{ maxWidth: '800px', margin: '1.5rem auto 0' }}>
            Transform your workplace through relationship wellness and emotional intelligence
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="corporate-intro-section">
        <div className="container">
          <div className="corporate-intro-content">
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>Relationship Wellness is a Business Advantage</h2>
            <p className="body-large" style={{ marginBottom: '1.5rem' }}>
              I work with corporates and organisations to deliver workshops that help employees navigate their relationships, marriages, and emotional challenges.
            </p>
            <p className="body-large" style={{ marginBottom: '1.5rem' }}>
              I believe that when a person's inner world and relationships are harmonized, it naturally reflects in their work.
            </p>
            <p className="body-large">
              When people feel safe, connected, and understood — both at home and within themselves — they show up to work more effective, creative, and resilient. <strong>Relationship wellness is not a soft skill; it is a business advantage.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>The Impact on Your Organization</h2>
          <div className="benefits-grid">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="benefit-card hover-lift">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>{benefit.title}</h3>
                <p className="body-small">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workshops */}
      <section className="workshops-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '3rem' }}>Workshop Programs</h2>
          <div className="workshops-grid">
            {workshops.map((workshop) => (
              <div key={workshop.title} className="workshop-card">
                <h3 className="heading-3" style={{ marginBottom: '0.75rem' }}>{workshop.title}</h3>
                <p className="body-medium" style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                  {workshop.description}
                </p>
                <div className="workshop-duration">
                  <span className="body-small font-mono">{workshop.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="corporate-gallery-section">
        <div className="container">
          <h2 className="heading-1" style={{ textAlign: 'center', marginBottom: '2rem' }}>Programs in Action</h2>
          <div className="corporate-gallery-grid">
            <img src="https://customer-assets.emergentagent.com/job_pleasure-medicine/artifacts/3ae7pr31_When%20women%20gather%20there%20is%20a%20special%20medicine%20and%20anointing%20that%20drops%20from%20above.%23kefodilemove.webp%20%283%29.jpg" alt="Corporate workshop" className="gallery-image" />
            <img src="https://customer-assets.emergentagent.com/job_pleasure-medicine/artifacts/v9hzdwf7_When%20women%20gather%20there%20is%20a%20special%20medicine%20and%20anointing%20that%20drops%20from%20above.%23kefodilemove.webp%20%282%29.jpg" alt="Team session" className="gallery-image" />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="corporate-cta-section">
        <div className="container">
          <div className="corporate-cta-content">
            <h2 className="heading-1" style={{ marginBottom: '1.5rem' }}>Invest in Your Team's Wellbeing</h2>
            <p className="body-large" style={{ marginBottom: '2rem' }}>
              Let's discuss how customized wellness programs can transform your organization.
            </p>
            <Link to="/contact" className="btn-primary">Schedule a Consultation</Link>
          </div>
        </div>
      </section>
    </div>
  );
};